import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Pencil, PlusCircle, Trash2 } from 'lucide-react';
import { platformKeysApi, adminApi } from '../../lib/api-client';
import type { PlatformApiKeySummary } from '../../lib/types';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input, Label, Textarea } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../../components/ui/dialog';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Badge } from '../../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../../components/ui/states';
import { useToast } from '../../components/ui/toast';

/**
 * BUI Super-Admin Console — Platform API key management (docs/external-api). A key is a GENERIC
 * B2B integration credential, enabled for any partner by a BUI Owner/Admin here — never specific
 * to one named customer — see CLAUDE.md. Modeled closely on ApiKeys.tsx's "create -> reveal raw
 * key once -> list -> revoke" flow, with an added tenant picker (which workspace this key's scans
 * run in) and scope checkboxes.
 */

const SCOPE_OPTIONS = ['external:scan', 'external:read'] as const;
const SCOPE_LABEL: Record<(typeof SCOPE_OPTIONS)[number], string> = {
  'external:scan': 'Dispatch scans',
  'external:read': 'Read scan results',
};

function CreateKeyForm({ onCreated }: { onCreated: (rawKey: string) => void }) {
  const queryClient = useQueryClient();
  const workspacesQuery = useQuery({ queryKey: ['admin', 'workspaces'], queryFn: () => adminApi.listWorkspaces() });
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [scopes, setScopes] = useState<string[]>([...SCOPE_OPTIONS]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [rateLimitPerHour, setRateLimitPerHour] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      platformKeysApi.create({
        name,
        tenantId,
        scopes,
        ...(webhookUrl.trim() ? { webhookUrl: webhookUrl.trim() } : {}),
        ...(rateLimitPerHour.trim() ? { rateLimitPerHour: Number(rateLimitPerHour) } : {}),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['platform-keys'] });
      onCreated(result.rawKey);
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const toggleScope = (scope: string) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  const workspaces = workspacesQuery.data?.workspaces ?? [];

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="platform-key-name">Name</Label>
        <Input id="platform-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="see.tech Production" required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-tenant">Workspace this key's scans run in</Label>
        <Select value={tenantId} onValueChange={setTenantId}>
          <SelectTrigger id="platform-key-tenant">
            <SelectValue placeholder="Select a workspace" />
          </SelectTrigger>
          <SelectContent>
            {workspaces.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label>Scopes</Label>
        {SCOPE_OPTIONS.map((scope) => (
          <label key={scope} className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} />
            {SCOPE_LABEL[scope]}
          </label>
        ))}
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-webhook">Webhook URL (optional — a per-scan value overrides this default)</Label>
        <Input id="platform-key-webhook" type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://partner.example/webhooks/defendiq" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-rate-limit">Rate limit per hour (optional — default 100)</Label>
        <Input id="platform-key-rate-limit" type="number" min={1} value={rateLimitPerHour} onChange={(e) => setRateLimitPerHour(e.target.value)} placeholder="100" />
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending || !name.trim() || !tenantId || scopes.length === 0}>
        Create key
      </Button>
    </form>
  );
}

function RevealKey({ rawKey, onClose }: { rawKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-muted">This is the only time you'll see this key in full. Copy it now and send it to the partner over a secure channel.</p>
      <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-sunken px-3 py-2">
        <code className="truncate text-sm text-ink">{rawKey}</code>
        <Button variant="ghost" size="sm" onClick={copy}>
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>
      <Button className="w-full" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}

/** Diffs the form state against the key as last loaded and returns only the fields that changed,
 * matching the API's partial-update contract (only send what the operator actually edited). An
 * empty object means nothing changed. */
function buildPatch(
  key: PlatformApiKeySummary,
  form: { name: string; webhookUrl: string; rateLimitPerHour: string; allowedIps: string; isActive: boolean },
): Parameters<typeof platformKeysApi.update>[1] {
  const patch: Parameters<typeof platformKeysApi.update>[1] = {};

  const trimmedName = form.name.trim();
  if (trimmedName && trimmedName !== key.name) patch.name = trimmedName;

  const trimmedWebhook = form.webhookUrl.trim();
  if (trimmedWebhook !== (key.webhookUrl ?? '')) patch.webhookUrl = trimmedWebhook ? trimmedWebhook : null;

  const parsedRateLimit = Number(form.rateLimitPerHour);
  if (form.rateLimitPerHour.trim() && Number.isInteger(parsedRateLimit) && parsedRateLimit !== key.rateLimitPerHour) {
    patch.rateLimitPerHour = parsedRateLimit;
  }

  const parsedIps = form.allowedIps
    .split('\n')
    .map((ip) => ip.trim())
    .filter(Boolean);
  if (JSON.stringify(parsedIps) !== JSON.stringify(key.allowedIps)) patch.allowedIps = parsedIps;

  if (form.isActive !== key.isActive) patch.isActive = form.isActive;

  return patch;
}

function EditKeyForm({ keyRecord, onSaved }: { keyRecord: PlatformApiKeySummary; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState(keyRecord.name);
  const [webhookUrl, setWebhookUrl] = useState(keyRecord.webhookUrl ?? '');
  const [rateLimitPerHour, setRateLimitPerHour] = useState(String(keyRecord.rateLimitPerHour ?? 100));
  const [allowedIps, setAllowedIps] = useState(keyRecord.allowedIps.join('\n'));
  const [isActive, setIsActive] = useState(keyRecord.isActive);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (patch: Parameters<typeof platformKeysApi.update>[1]) => platformKeysApi.update(keyRecord.id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-keys'] });
      toast({ title: 'Key updated', variant: 'success' });
      onSaved();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const patch = buildPatch(keyRecord, { name, webhookUrl, rateLimitPerHour, allowedIps, isActive });
    if (Object.keys(patch).length === 0) {
      onSaved();
      return;
    }
    mutation.mutate(patch);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <Label htmlFor="platform-key-edit-prefix">Key</Label>
        <p id="platform-key-edit-prefix" className="text-xs text-ink-muted">
          {keyRecord.keyPrefix}•••••••• (read-only — generate a new key to rotate it)
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-edit-name">Name</Label>
        <Input id="platform-key-edit-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-edit-webhook">Webhook URL</Label>
        <Input
          id="platform-key-edit-webhook"
          type="url"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-edit-rate-limit">Rate limit per hour</Label>
        <Input
          id="platform-key-edit-rate-limit"
          type="number"
          min={1}
          value={rateLimitPerHour}
          onChange={(e) => setRateLimitPerHour(e.target.value)}
          placeholder="100"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="platform-key-edit-ips">IP allowlist (one per line)</Label>
        <Textarea
          id="platform-key-edit-ips"
          value={allowedIps}
          onChange={(e) => setAllowedIps(e.target.value)}
          placeholder={'203.0.113.4\n198.51.100.0/24'}
          rows={4}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        Active
      </label>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onSaved}>
          Cancel
        </Button>
        <Button type="submit" disabled={mutation.isPending || !name.trim()}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

export function PlatformKeys() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [editKey, setEditKey] = useState<PlatformApiKeySummary | null>(null);
  const query = useQuery({ queryKey: ['platform-keys'], queryFn: () => platformKeysApi.list() });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => platformKeysApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-keys'] });
      toast({ title: 'Key revoked', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not revoke key', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const keys = query.data?.keys ?? [];

  return (
    <div>
      <PageHeader
        title="Platform API keys"
        description="Generic B2B partner integration credentials — any partner can be enabled here, for any workspace."
        action={
          <Dialog
            open={createOpen || revealKey !== null}
            onOpenChange={(open) => {
              if (!open) {
                setCreateOpen(false);
                setRevealKey(null);
              } else {
                setCreateOpen(true);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button>
                <PlusCircle size={16} /> New Platform API key
              </Button>
            </DialogTrigger>
            <DialogContent title={revealKey ? 'Key created' : 'New Platform API key'} description={revealKey ? undefined : "Choose a name, workspace, and scopes for this partner's key."}>
              {revealKey ? (
                <RevealKey
                  rawKey={revealKey}
                  onClose={() => {
                    setRevealKey(null);
                    setCreateOpen(false);
                  }}
                />
              ) : (
                <CreateKeyForm
                  onCreated={(rawKey) => {
                    setCreateOpen(false);
                    setRevealKey(rawKey);
                  }}
                />
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <Dialog open={editKey !== null} onOpenChange={(open) => !open && setEditKey(null)}>
        <DialogContent title="Edit Platform API key" description="The key value itself can't be changed — generate a new key to rotate it.">
          {editKey ? <EditKeyForm keyRecord={editKey} onSaved={() => setEditKey(null)} /> : null}
        </DialogContent>
      </Dialog>

      {keys.length === 0 ? (
        <EmptyState icon={<KeyRound size={32} />} title="No Platform API keys yet" description="Create one to let a partner dispatch scans and receive results programmatically." />
      ) : (
        <div className="space-y-2">
          {keys.map((key) => (
            <Card key={key.id}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
                <div>
                  <p className="font-medium text-ink">{key.name}</p>
                  <p className="text-xs text-ink-muted">
                    {key.keyPrefix}•••••••• · created {new Date(key.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-ink-muted">
                    Last used: {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'} · {key.requestCount} request{key.requestCount === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {key.scopes.map((scope) => (
                    <Badge key={scope} variant="brand">
                      {scope}
                    </Badge>
                  ))}
                  {key.isActive ? (
                    <>
                      <Button variant="ghost" size="icon" aria-label="Edit key" onClick={() => setEditKey(key)}>
                        <Pencil size={16} />
                      </Button>
                      <ConfirmDialog
                        trigger={
                          <Button variant="ghost" size="icon" aria-label="Revoke key">
                            <Trash2 size={16} className="text-danger" />
                          </Button>
                        }
                        title="Revoke this key?"
                        description={`"${key.name}" will stop working immediately. Any integration using it will fail until replaced with a new key.`}
                        confirmLabel="Revoke"
                        variant="danger"
                        onConfirm={() => revokeMutation.mutateAsync(key.id)}
                      />
                    </>
                  ) : (
                    <Badge variant="danger">Revoked</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
